import { FiSliders } from 'react-icons/fi';

export const advancedModalsData = {
  courseId: 'advanced-modals',
  courseType: 'grammar',
  title: 'Advanced Modal Patterns',
  icon: FiSliders,
  overview: {
    impact: 'Express nuanced certainty, obligation, and criticism',
    focus: 'Modal perfect, continuous, and passive forms',
    uses: 'Writing Task 2, Speaking Part 3',
    duration: '25 minutes'
  },
  sections: [
    {
      title: 'Modal Perfect (Past Deduction/Regret)',
      content: `<p><strong>modal + have + past participle</strong></p>`,
      examples: [
        {
          label: 'Certainty',
          text: 'She must have left already. (I\'m almost certain.)'
        },
        {
          label: 'Possibility',
          text: 'He might have missed the bus. (It\'s possible.)'
        },
        {
          label: 'Criticism',
          text: 'You should have studied harder. (But you didn\'t.)'
        }
      ]
    },
    {
      title: 'Degrees of Certainty',
      content: `<p>Modals express different levels of certainty:</p>`,
      table: {
        headers: [
          'Certainty',
          'Present',
          'Past'
        ],
        rows: [
          [
            '100%',
            'She must be at home.',
            'She must have been at home.'
          ],
          [
            'Possible',
            'She could/may/might be at home.',
            'She could/may/might have been at home.'
          ],
          [
            'Impossible',
            'She can\'t be at home.',
            'She can\'t have been at home.'
          ]
        ]
      }
    },
    {
      title: 'Should Have / Could Have',
      content: `<p>Expressing criticism or regret about the past:</p>`,
      points: [
        'should have + pp = it was the right thing but didn\'t happen: \'They should have invested in renewable energy.\'',
        'could have + pp = it was possible but didn\'t happen: \'We could have prevented the crisis.\'',
        'needn\'t have + pp = did something unnecessary: \'You needn\'t have worried.\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Correct Modal',
      type: 'select',
      instructions: 'Select the most appropriate form.',
      questions: [
        {
          text: 'She\'s not answering. She ___ sleeping. (almost certain)',
          options: [
            'must be',
            'might be',
            'should be'
          ],
          answer: 'must be',
          explanation: 'High certainty → \'must be\'.'
        },
        {
          text: 'You ___ told me earlier! (criticism)',
          options: [
            'should have',
            'must have',
            'could have'
          ],
          answer: 'should have',
          explanation: 'Criticism about past action → \'should have\'.'
        },
        {
          text: 'He ___ the train — he\'s always late. (possibility)',
          options: [
            'might have missed',
            'must have missed',
            'should have missed'
          ],
          answer: 'might have missed',
          explanation: 'Possibility, not certainty → \'might have\'.'
        }
      ]
    }
  ],
  tips: [
    'must have = almost certain deduction about the past',
    'might/could have = possibility about the past',
    'should have = criticism or regret',
    'can\'t have = impossibility about the past'
  ],
  dailyPractice: [
    'Practice advanced modal patterns concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Advanced Modals: Modal + have + past participle for past deduction, possibility, and criticism. Degrees of certainty with must/might/can\'t. Band 7+ grammar.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...advancedModalsData,
      title: advancedModalsData.title + ' (Foundations)',
      overview: { ...advancedModalsData.overview, duration: '15-20 minutes' },
      sections: advancedModalsData.sections.slice(0, Math.max(2, Math.ceil(advancedModalsData.sections.length / 2))),
      exercises: advancedModalsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...advancedModalsData,
      title: advancedModalsData.title + ' (Advanced)',
      overview: { ...advancedModalsData.overview, duration: '30-35 minutes' },
      sections: advancedModalsData.sections,
      exercises: advancedModalsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(advancedModalsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return advancedModalsData;
}
